"use client";
// @ts-strict-ignore
import * as React from "react";
import BuyingGuideBanner from "./HowToBuyBanner";
import {
  HowToBuyPageContainer,
  HowToBuyPageContainerDarkSection,
  PricesPageRedirectContainer
} from "./style";
import BuyingSteps from "./BuyingSteps";
import LatestOnCoin from "./LatestOnCoin";
import TrendingTokens from "./TrendingTokens";
import OtherCryptoAssets from "./OtherCryptoAssets";
import HowToBuyFAQs from "./HowToBuyFAQs";
import { Colors } from "themes/color-palette";
import dayjs from "dayjs";
import { useTranslations } from "i18n";
import { useWidthObserver } from "hooks/useWidthObserver";
import { HubbleDisplay } from "components/parts/Typography";
import NextLink from "components/parts/NextLink";
import Button from "components/parts/button";
import { useHowToBuyDataFetcher } from "./hooks/useHowToBuyDataFetcher";
import type { EntryCollection } from "contentful";
import type { Locale } from "i18n/constants";
import type { AllPricesResponse } from "utils/prices";
import type { CoinDetail, FAQItemContentModel } from "types/contentful";
import type { PriceOverview } from "types/prices";
import type { PostPropType } from "types/blog-type";
import type { TrendingCoinObj } from "types/howToBuy-type";

interface HowToBuyPageProps {
  slug: string;
  locale: Locale;
  coinDetail?: CoinDetail & { priceOverview: PriceOverview };
  otherCoinsToBuy?: Array<AllPricesResponse>;
  trendingCoins?: Array<TrendingCoinObj>;
  relatedArticles?: PostPropType[];
  relatedFaqs?: EntryCollection<FAQItemContentModel>;
}

const HowToBuyPage = (props: HowToBuyPageProps) => {
  const { data } = useHowToBuyDataFetcher(props.slug, props.locale);
  const {
    currentCoinDetail: coinDetail,
    trendingCoins,
    otherCoinsToBuy,
    relatedFaqs
  } = data ?? {};

  const { isMobile } = useWidthObserver();
  const t = useTranslations("how-to-buy");
  const { coin, priceOverview, name, slug } = coinDetail || {};

  return (
    <HowToBuyPageContainer>
      <BuyingGuideBanner
        coinName={name}
        priceOverview={priceOverview}
        slug={slug}
        coinSymbol={coin}
        lastUpdated={dayjs().format("MMM D, YYYY").toUpperCase()}
        isMobile={isMobile}
      />
      <BuyingSteps coinName={name} isMobile={isMobile} />
      <PricesPageRedirectContainer>
        <HubbleDisplay
          mb={3}
          style={{ maxWidth: "605px" }}
          margin={"auto"}
          as={"h2"}
          size="sm"
          color={Colors.black}
          semiBold
        >
          {t("visitPricePage") + " " + name}
        </HubbleDisplay>
        <NextLink href={`https://www.gemini.com/prices/${slug}`}>
          <Button isBlue style={{ margin: "auto" }}>
            {t("goToPrices")}
          </Button>
        </NextLink>
      </PricesPageRedirectContainer>
      <LatestOnCoin
        coinName={name}
        posts={props.relatedArticles}
        isMobile={isMobile}
      />
      <TrendingTokens trendingCoins={trendingCoins} />
      <HowToBuyPageContainerDarkSection>
        <OtherCryptoAssets otherCoinsToBuy={otherCoinsToBuy} />
        <HowToBuyFAQs relatedFaqs={relatedFaqs} />
        <PricesPageRedirectContainer isDark>
          <HubbleDisplay
            mb={3}
            style={{ maxWidth: "605px" }}
            margin={"auto"}
            as={"h2"}
            size="lg"
            bold
            color={Colors.white}
          >
            {t("growPortfolio")} <p style={{ margin: 0 }}>Buy {name} today</p>
          </HubbleDisplay>
          <NextLink href={`https://www.gemini.com/prices/${slug}`}>
            <Button isBlue style={{ margin: "auto" }}>
              {t("buyCoin") + " " + name}
            </Button>
          </NextLink>
        </PricesPageRedirectContainer>
      </HowToBuyPageContainerDarkSection>
    </HowToBuyPageContainer>
  );
};

export default HowToBuyPage;
